import React, { useMemo } from 'react';
import { useQuery } from 'react-query';

import { getContractBenefits } from 'apis/CommunityAPI';
import DynamicColumnTable from 'components/DynamicColumnsTable/DynamicColumnsTable';
import { usePublicationContext } from 'components/Publication/PublicationContext';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { useAppSelector } from 'store/hook';
import { CommunityContract } from 'types/contracts';

import { getContractBenefitsTableColumns, getContractBenefitsTableData } from './utils';
import { useWidgetContext } from '../Widget/WidgetContext';

const ContractBenefits = () => {
  const { contracts } = useAppSelector((state) => state.global);
  const { community, communityId, competitors, competitorId, view } = useCommunityViewContext();
  const { filters } = useWidgetContext();
  const { publication } = usePublicationContext();

  const competitorsList = competitorId
    ? [(competitors || []).find((competitor) => competitor.id == competitorId)].filter(Boolean) // if on Competitor screen, we show only that competitors in table
    : [
        ...(competitors || []).filter(
          (competitor: any) => !filters.competitors.length || filters.competitors.includes(competitor.id)
        ),
      ];

  const selectedCommunityContract = useMemo(() => {
    if (!filters.communityContractType || !community?.contracts?.length) {
      return undefined;
    }

    return (community.contracts as CommunityContract[]).find(
      (contract: CommunityContract) => contract.id === filters.communityContractType
    );
  }, [community?.contracts, filters.communityContractType]);

  const selectedContractTypeId = selectedCommunityContract?.contract_id;
  const isDefaultView = Boolean(view?.default);

  const contractTypeFilter: number[] | undefined = useMemo(() => {
    if (isDefaultView) {
      return undefined;
    }

    if (filters.contractTypes?.length) {
      return filters.contractTypes;
    }

    if (selectedContractTypeId !== undefined) {
      return [selectedContractTypeId];
    }

    return undefined;
  }, [filters.contractTypes, isDefaultView, selectedContractTypeId]);

  const competitorIds = useMemo(() => (competitors ?? []).map((competitor: any) => competitor.id), [competitors]);
  const serializedContractTypeFilter = useMemo(
    () => (contractTypeFilter && contractTypeFilter.length ? [...contractTypeFilter].sort().join(',') : 'all'),
    [contractTypeFilter]
  );

  const { data: contractBenefits } = useQuery({
    queryKey: ['contractBenefits', communityId, competitorIds, serializedContractTypeFilter],
    queryFn: async () => {
      if (!communityId) {
        return [];
      }
      const allCommunityIds = [communityId, ...(competitors ?? []).map((competitor) => competitor.id)];
      const promises = (allCommunityIds || []).map((communityId) =>
        getContractBenefits(communityId, {
          community_id: [communityId],
          contract_types: contractTypeFilter,
          ...(publication?.hash ? { public_hash: publication.hash } : {}),
        })
      );
      const results = await Promise.all(promises);
      return results.flatMap((response) => response);
    },
    refetchOnWindowFocus: false,
    enabled: communityId > 0,
  });

  const dataSource = getContractBenefitsTableData(
    contracts,
    contractBenefits,
    [community, ...competitorsList].filter(Boolean),
    contractTypeFilter
  );

  return (
    <DynamicColumnTable
      className="contract-benefits"
      dataSource={dataSource}
      community={community}
      competitors={competitorsList}
      generateTableColumns={getContractBenefitsTableColumns}
    />
  );
};

export default ContractBenefits;
